import React from 'react'
import { motion } from 'framer-motion'
import { Trans, useTranslation } from 'react-i18next'
import { FaArrowLeft, FaCheckCircle, FaTimes } from 'react-icons/fa'
import styled, { css } from 'styled-components'
import { AnimatedContentLoader, Button } from '@root/components'
import { getDashboardPageUrl } from '@root/constants'
import { useBusinessContext } from '@root/context'
import { BusinessForms } from '@root/query'
import poweredByHolvi from '@assets/powered-by-holvi-white.svg'

const COLORS = {
  primary: '#45aefb',
  primaryDark: '#1b98f5',
  bg: '#031e33',
  border: '#105b93',
  textSubtle: '#b8e1ff'
} as const

type PlanMeta = {
  key: 'flex' | 'business'
  ctaLink?: string | null
}

const PLANS: PlanMeta[] = [
  {
    key: 'flex',
    ctaLink: 'https://login.app.holvi.com/register?lang=fi&country=FI&promo=VAR-NOCFO-FLEX'
  },
  {
    key: 'business',
    ctaLink: 'https://login.app.holvi.com/register?lang=fi&country=FI&promo=VAR-NOCFO-BUSINESS'
  }
]

export const BusinessAccountsPage: React.FC = () => {
  const { t } = useTranslation()
  const { slug, data } = useBusinessContext()
  const homePage = getDashboardPageUrl(slug)
  const holviExistingCustomerGuideLink = t('link.docs.holviExistingCustomerGuide')

  const isPlanEligible = (plan: PlanMeta['key']) => {
    if (!data?.form) return true
    if (plan === 'flex')
      return (
        data?.form === BusinessForms.FI_TMI ||
        data?.form === BusinessForms.FI_OY ||
        data?.form === BusinessForms.FI_OYJ ||
        data?.form === BusinessForms.FI_AS_OY ||
        data?.form === BusinessForms.FI_KOY
      )
    if (plan === 'business')
      return (
        data?.form === BusinessForms.FI_OY ||
        data?.form === BusinessForms.FI_OYJ ||
        data?.form === BusinessForms.FI_AS_OY
      )
    return false
  }

  return (
    <AnimatedContentLoader isLoading={data?.id === undefined}>
      <Page>
        <CloseButtonLink href={homePage}>
          <FaTimes />
        </CloseButtonLink>

        <Content initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }}>
          <h1>{t('businessAccounts.title')}</h1>

          <h4>
            {t('businessAccounts.subtitle')}{' '}
            <a href="https://nocfo.io/page/business-accounts" target="_blank" rel="noreferrer">
              {t('businessAccounts.learnMore')}
            </a>
          </h4>

          <Cards>
            {PLANS.map(({ key, ctaLink }) => {
              const base = `businessAccounts.plans.${key}` as const
              const features = t(`${base}.features`, { returnObjects: true }) as string[]
              const eligible = isPlanEligible(key)

              return (
                <Card key={key} $disabled={!eligible}>
                  <CardHead>
                    <h1>{t(`${base}.name`)}</h1>
                    <p>{t(`${base}.description`)}</p>

                    {!eligible && (
                      <NotEligibleWarning>
                        <FaTimes /> <span>{t(`${base}.eligibility`)}</span>
                      </NotEligibleWarning>
                    )}
                  </CardHead>

                  <PriceRow>
                    <span className="amount">{t(`${base}.price`)}</span>
                    <span className="unit">{t('businessAccounts.perMonth')}</span>
                  </PriceRow>

                  <Features>
                    {features.map(feature => (
                      <FeatureRow key={feature}>
                        <FaCheckCircle />
                        <span>{feature}</span>
                      </FeatureRow>
                    ))}
                  </Features>

                  <ButtonLink
                    href={eligible ? ctaLink ?? undefined : undefined}
                    aria-disabled={!eligible || !ctaLink}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <PrimaryButton disabled={!eligible || !ctaLink}>
                      {t(`${base}.cta`)}
                    </PrimaryButton>
                  </ButtonLink>
                </Card>
              )
            })}
          </Cards>

          <ExistingCustomerInfo>
            <Trans
              i18nKey="businessAccounts.existingAccountInfo"
              components={[
                <a
                  key="link"
                  href={holviExistingCustomerGuideLink}
                  target="_blank"
                  rel="noreferrer"
                />
              ]}
            />
          </ExistingCustomerInfo>

          <a
            href="https://www.nocfo.io/fi/page/business-accounts#Powered-by-Holvi"
            target="_blank"
            rel="noreferrer"
          >
            <img src={poweredByHolvi} width={120} alt="Powered by Holvi" />
          </a>

          <BackLink href={homePage}>
            <FaArrowLeft />
            <span>{t('businessAccounts.back')}</span>
          </BackLink>
        </Content>
      </Page>
    </AnimatedContentLoader>
  )
}

const sharedCardShadow = css`
  border-color: ${COLORS.primaryDark};
  box-shadow: 0 0 8px 0 ${COLORS.primaryDark}, 0 0 16px 0 ${COLORS.primary}66;
`

const Page = styled.div`
  position: relative;
  width: 100%;
  min-height: 100dvh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background: ${({ theme }) => theme.colors.menuBg};
  padding: ${({ theme }) => theme.spacing.xxl}rem;
`

const CloseButtonLink = styled.a`
  position: absolute;
  top: 1.5rem;
  right: 1.5rem;
  width: 3rem;
  height: 3rem;
  border-radius: 50%;
  background: #000;
  border: 1px solid ${COLORS.border};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${COLORS.textSubtle};
  transition: border-color 0.2s ease, color 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    ${sharedCardShadow};
    color: ${COLORS.primary};
  }

  svg {
    width: 1.25rem;
    height: 1.25rem;
  }
`

const Content = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  color: ${({ theme }) => theme.colors.neutralWhite};

  h1 {
    font-size: ${({ theme }) => theme.fontSize.xxl}rem;
    margin: 0;
  }

  h4 {
    font-size: ${({ theme }) => theme.fontSize.md}rem;
    color: ${COLORS.textSubtle};
    margin-top: ${({ theme }) => theme.spacing.sm}rem;
    text-align: center;

    a {
      color: ${COLORS.textSubtle};
      text-decoration: underline;
    }
  }
`

const Cards = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.md}rem;
  max-width: 850px;
  margin-top: ${({ theme }) => theme.spacing.lg}rem;
  margin-bottom: ${({ theme }) => theme.spacing.md}rem;
`

const Card = styled.div<{ $disabled?: boolean }>`
  border: 1px solid ${COLORS.border};
  background: ${COLORS.bg};
  background-image: linear-gradient(#0000, #000);
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
  width: 100%;
  max-width: 400px;
  min-width: 300px;
  padding: ${({ theme }) => theme.spacing.xl}rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xl}rem;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    ${({ $disabled }) => ($disabled ? '' : sharedCardShadow)};
  }
`

const CardHead = styled.div`
  text-align: center;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  min-height: 120px;

  h1 {
    font-size: ${({ theme }) => theme.fontSize.xl}rem;
  }

  p {
    font-size: ${({ theme }) => theme.fontSize.md}rem;
    color: ${COLORS.textSubtle};
    margin: 0;
  }
`

const NotEligibleWarning = styled.div`
  margin-top: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  color: ${({ theme }) => theme.colors.error};

  svg {
    width: 1rem;
    height: 1rem;
  }
`

const PriceRow = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs}rem;

  .amount {
    font-size: 2.4rem;
    font-weight: bold;
  }

  .unit {
    color: ${COLORS.textSubtle};
    font-size: ${({ theme }) => theme.fontSize.md}rem;
  }
`

const Features = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.lg}rem;
  min-height: 300px;
`

const FeatureRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  color: ${COLORS.textSubtle};

  svg {
    fill: ${COLORS.primary};
    width: ${({ theme }) => theme.iconSize.sm}rem;
    height: ${({ theme }) => theme.iconSize.sm}rem;
    min-width: ${({ theme }) => theme.iconSize.sm}rem;
    min-height: ${({ theme }) => theme.iconSize.sm}rem;
  }
`

const BackLink = styled.a`
  margin-top: ${({ theme }) => theme.spacing.lg}rem;
  color: ${({ theme }) => theme.colors.neutralWhite};
  font-size: ${({ theme }) => theme.fontSize.md}rem;
  text-decoration: none;
  transition: color 0.2s ease;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs}rem;

  &:hover {
    color: ${COLORS.primary};
  }
`

const PrimaryButton = styled(Button).attrs({
  style: {
    background: COLORS.primary,
    color: COLORS.bg,
    width: '100%',
    justifyContent: 'center',
    padding: '1.4rem'
  }
})``

const ButtonLink = styled.a`
  width: 100%;
`

const ExistingCustomerInfo = styled.p`
  text-align: center;
  max-width: 600px;
  color: ${COLORS.textSubtle};

  a {
    color: ${COLORS.textSubtle};
    text-decoration: underline;
  }
`
